/**
 * Admin Navigation Panel Component
 * Displays admin navigation links in a rich panel format
 * Only visible to authorized admin users on the Cache Admin page
 */

import React, { useMemo } from "react";
import { Stack } from "@fluentui/react";
import { Box, Typography, Divider, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import {
    Database20Regular,
    Shield20Regular,
    ChevronRight20Regular
} from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { isUserAuthorizedForCacheAdmin } from "../config/CacheAdminConfig";

interface AdminNavItem {
    id: string;
    label: string;
    description: string;
    icon: React.ReactNode;
    route: string;
    isActive?: boolean;
}

const AdminNavigationPanel: React.FC<{ currentPage?: string }> = ({ currentPage = "cache-admin" }) => {
    const navigate = useNavigate();
    const userEmail = sessionStorage.getItem("loginUserName");
    const isAuthorized = useMemo(() => isUserAuthorizedForCacheAdmin(userEmail), [userEmail]);

    // Only render if user is authorized AND we're on a page that has admin navigation
    if (!isAuthorized || !currentPage) {
        return null;
    }

    const navItems: AdminNavItem[] = [
        {
            id: "cache-admin",
            label: "Cache Administration",
            description: "Manage system caches",
            icon: <Database20Regular style={{ fontSize: 20, color: "#0078D4" }} />,
            route: "/cacheadmin",
            isActive: currentPage === "cache-admin"
        }
    ];

    const handleNavigate = (route: string) => {
        if (route !== "#") {
            navigate(route);
        }
    };

    return (
        <Box sx={{ width: "100%", mb: 2 }}>
            {/* Section Header */}
            <Box sx={{ px: 2, py: 1.5 }}>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <Shield20Regular style={{ fontSize: 16, color: "#0078D4" }} />
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 700,
                            fontSize: 11,
                            color: "#605E5C",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                        }}
                    >
                        Admin Navigation
                    </Typography>
                </Stack>
            </Box>

            <Divider sx={{ mb: 1 }} />

            {/* Navigation List */}
            <List sx={{ py: 0 }}>
                {navItems.map((item) => (
                    <ListItemButton
                        key={item.id}
                        onClick={() => handleNavigate(item.route)}
                        disabled={item.route === "#"}
                        sx={{
                            py: 1.5,
                            px: 2,
                            backgroundColor: item.isActive ? "#EFF6FC" : "transparent",
                            borderLeft: item.isActive ? "3px solid #0078D4" : "3px solid transparent",
                            "&:hover": {
                                backgroundColor: item.route !== "#" ? "#F5F7FA" : "transparent",
                            },
                            opacity: item.route === "#" ? 0.6 : 1,
                            cursor: item.route === "#" ? "default" : "pointer",
                            transition: "all 0.2s ease"
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 40,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {item.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: 12,
                                        color: item.isActive ? "#0078D4" : "#1A1A2E"
                                    }}
                                >
                                    {item.label}
                                </Typography>
                            }
                            secondary={
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: 10,
                                        color: "#8A8886"
                                    }}
                                >
                                    {item.description}
                                </Typography>
                            }
                        />
                        {item.route !== "#" && (
                            <ChevronRight20Regular style={{ fontSize: 16, color: "#8A8886" }} />
                        )}
                    </ListItemButton>
                ))}
            </List>
        </Box>
    );
};

export default AdminNavigationPanel;
